import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchConfirmationPhase, fetchPatientData, createConfirmationPhase, updateConfirmationPhase } from "../api/confirmationApi";
import { mapBackendToForm, mapFormToBackend } from "../utils/mapping";
import { useFormValidation } from "./useFormValidation";
import { useNotification } from "../../../../contexts/NotificationContext";
import { DRAFT_KEYS, loadDraft, clearDraft } from "../../../../utils/draftUtils";
import { validateConfirmationForm } from "../utils/validation";

export const useConfirmationForm = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [success, setSuccess] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    uhid_no: uhid_no || '',
    entry_date_time: '',
    bp: '',
    heart_rate: '',
    time: '',
    oxygen_levels: '',
    temperature: '',
    last_medication_taken: '',
    dosage: '',
    fasting_confirmation: false,
    fasting_last_time: '',
    covid_screening: false,
    covid_screening_date: '',
    Comments_if_pending: '',
    blood_transfusion_consent: false,
    imaging_tests_reviewed: false,
    all_lab_results_verified: false,
    cardiac_clearance: false,
    imaging_tests: '',
    anaesthesia_type: '',
    pre_anaesthetic_medication_given: false,
    pre_anaesthetic_medication_details: '',
    patient_sedation_level: false,
    anaesthesiologist_clearance: false,
    anaesthesiologist_name: '',
    anaesthesiologist_signature: '',
    surgical_team_assigned: '',
    surgical_instruments_ready: false,
    ot_sterilization_check: false,
    blood_products_available: false,
    guardian_consent: false,
    anaesthesia_consent: false,
    legal_representative_name: '',
    legal_representative_contact: '',
    consent_date_time: '',
    antibiotics_given: false,
    antibiotics_drug_name: '',
    antibiotics_dosage: '',
    antibiotics_time: '',
    anti_coagulants_stopped: false,
    skin_prep_completed: false,
    units_reserved: '',
    pre_surgical_clothing_checked: false,
    blood_type: '',
    blood_cross_matching: '',
    allergy_review: '',
    blood_units_reserved: '',
    blood_units_reserved1: false,
    specify_allergies: '',
    final_physician_approval: false,
    doctor_name: '',
    doctor_signature: ''
  });

  const { errors, clearError, validateSubmit, setErrors } = useFormValidation(formData);

  // Fetch Patient Data
  const { data: patientData, isLoading: patientLoading } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  // Fetch Confirmation Phase Data
  const { data: existingData, isLoading: dataLoading } = useQuery({
    queryKey: ['confirmationPhase', uhid_no],
    queryFn: () => fetchConfirmationPhase(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  // Effect to handle fetched data
  useEffect(() => {
    if (existingData && existingData.entry_date_time) {
      const mapped = mapBackendToForm(existingData);
      setFormData(prev => ({ ...prev, ...mapped }));
      setSuccess(true);
    }
  }, [existingData]);

  // Effect to handle patient data
  useEffect(() => {
    if (patientData) {
      setFormData(prev => ({
        ...prev,
        uhid_no: patientData.uhid_no,
        name: patientData.name || ''
      }));
    }
  }, [patientData]);

  // Handle draft
  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.CONFIRMATION_PHASE, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no]);

  const handleChange = useCallback((e) => {
    const { name, type, checked, value } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      
      // Special logic for fasting
      if (name === 'fasting_confirmation' && checked) {
        updated.fasting_last_time = new Date().toISOString().slice(0, 16);
      }
      
      return updated;
    });

    if (errors[name]) {
      clearError(name);
    }
  }, [errors, clearError]);

  const handleNameChange = useCallback((e) => {
    const { name, value } = e.target;
    const regex = /^[a-zA-Z\s.-]*$/;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        clearError(name);
      }
    }
  }, [errors, clearError]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    const newErrors = validateConfirmationForm({ ...formData, [name]: value });
    setErrors(prev => ({
      ...prev,
      [name]: newErrors[name]
    }));
  }, [formData, setErrors]);

  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = (signatureDataURL) => {
    setFormData(prev => ({
      ...prev,
      [currentSignatureRole]: signatureDataURL.split(',')[1]
    }));
    setSignatureDialogOpen(false);
  };

  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updateConfirmationPhase(uhid_no, data);
      } else {
        return createConfirmationPhase(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success 
        ? "Confirmation Phase Form updated successfully!" 
        : "Confirmation Phase Form submitted successfully!");
      
      queryClient.invalidateQueries(['confirmationPhase', uhid_no]);
      setSuccess(true);
      clearDraft(DRAFT_KEYS.CONFIRMATION_PHASE, uhid_no);
      navigate(`/mainPage/${uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: (error) => {
      console.error('Error submitting the form!', error);
      showNotification('error', `Error submitting form: ${error.message}`);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mutation.isLoading) return;

    if (!validateSubmit()) {
      return;
    }

    const backendData = mapFormToBackend(formData);
    console.log('SENDING TO BACKEND:', JSON.stringify(backendData, null, 2));
    mutation.mutate(backendData);
  };

  return {
    formData,
    setFormData,
    errors,
    loading: patientLoading || dataLoading || mutation.isLoading,
    success,
    signatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleBlur,
    setSignatureDialogOpen,
    handleSubmit
  };
};
